// /analysis/:slug — workspace con split:
//   Izquierda: módulos (configurables, editables, eliminables)
//   Derecha: chat IA que crea/edita/elimina módulos vía tool calls

import React, { useEffect, useRef, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { useParams, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaArrowLeft,
  FaPlus,
  FaRobot,
  FaUser,
  FaPaperPlane,
  FaTimes,
  FaExclamationCircle,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechButton,
  TechInput,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import {
  getPage,
  createModule,
  updateModule,
  deleteModule,
  sendChat,
} from '../../services/analysisService';
import { CitePopup, wrapWithCitations } from '../UI/Citations';
import AnalysisModule, { KIND_LABEL } from './AnalysisModule';

const dotBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30%           { transform: translateY(-4px); opacity: 1; }
`;

const BackLink = styled(Link)`
  display: inline-flex; align-items: center; gap: 0.5rem;
  color: var(--text-disabled);
  font-family: ${mono}; font-size: 0.7rem; letter-spacing: 0.16em;
  text-transform: uppercase; text-decoration: none;
  margin-bottom: 0.65rem;
  &:hover { color: ${accent}; }
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 1rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const ModulesCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
`;

const ChatCol = styled.div`
  position: sticky;
  top: 1rem;
  align-self: start;
  height: calc(100vh - 110px);
  display: flex;
  flex-direction: column;
  background: ${panelDeep};
  border: 1px solid rgba(0, 180, 216, 0.18);
  border-radius: 12px;
  overflow: hidden;
  @media (max-width: 1100px) {
    position: static;
    height: 60vh;
  }
`;

const ChatHeader = styled.div`
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${borderSoft};
  background: linear-gradient(180deg, rgba(0, 180, 216, 0.07), transparent);
  display: flex;
  align-items: center;
  gap: 0.6rem;
  .icon {
    width: 30px; height: 30px;
    border-radius: 50%;
    background: rgba(0, 180, 216, 0.15);
    border: 1px solid rgba(0, 180, 216, 0.35);
    color: ${accent};
    display: flex; align-items: center; justify-content: center;
  }
  .title { flex: 1; color: var(--text-primary); font-weight: 600; }
  .sub {
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const ChatBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.9rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  background: rgba(11, 19, 32, 0.25);
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 6px; }
`;

const Msg = styled.div`
  display: flex; gap: 0.6rem; align-items: flex-start;
  .avatar {
    flex-shrink: 0; width: 26px; height: 26px;
    border-radius: 50%;
    background: ${({ $role }) => ($role === 'user' ? 'rgba(0, 180, 216, 0.1)' : 'rgba(6, 214, 160, 0.1)')};
    border: 1px solid ${borderSoft};
    color: ${({ $role }) => ($role === 'user' ? accent : 'var(--alert-success)')};
    display: flex; align-items: center; justify-content: center;
    font-size: 0.72rem;
  }
  .bubble {
    flex: 1; min-width: 0;
    background: rgba(11, 19, 32, 0.55);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    padding: 0.6rem 0.8rem;
    color: var(--text-secondary);
    font-size: 0.84rem;
    line-height: 1.5;
    h1, h2, h3, h4 { color: var(--text-primary); margin: 0.4rem 0 0.25rem; font-size: 0.92rem; }
    p { margin: 0 0 0.4rem; } p:last-child { margin: 0; }
    ul, ol { margin: 0.25rem 0 0.4rem 1.1rem; padding: 0; }
    li { margin-bottom: 0.18rem; }
    table { border-collapse: collapse; margin: 0.4rem 0; font-size: 0.78rem; }
    th, td { border: 1px solid ${borderSoft}; padding: 3px 6px; }
    code { background: rgba(0,180,216,0.1); color: ${accent}; padding: 1px 4px; border-radius: 3px; font-size: 0.78rem; }
    a { color: ${accent}; }
  }
  .actions-summary {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--alert-success);
    margin-top: 4px;
  }
`;

const Typing = styled.div`
  display: inline-flex; gap: 4px; align-items: center;
  span {
    width: 5px; height: 5px; border-radius: 50%;
    background: ${accent};
    animation: ${dotBounce} 1.1s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

const InputWrap = styled.form`
  border-top: 1px solid ${borderSoft};
  padding: 0.7rem 0.9rem;
  background: rgba(11, 19, 32, 0.4);
  display: flex;
  gap: 0.5rem;
`;

const ChatInput = styled.textarea`
  flex: 1; resize: none;
  min-height: 38px; max-height: 110px;
  background: rgba(11, 19, 32, 0.65);
  border: 1px solid ${borderSoft};
  border-radius: 7px;
  color: var(--text-primary);
  font-family: inherit; font-size: 0.85rem;
  padding: 0.5rem 0.7rem; outline: none;
  &:focus { border-color: rgba(0, 180, 216, 0.5); }
`;

const SendBtn = styled.button`
  flex-shrink: 0;
  width: 38px; height: 38px;
  border-radius: 7px;
  border: 1px solid rgba(0, 180, 216, 0.35);
  background: rgba(0, 180, 216, 0.15);
  color: ${accent};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  &:hover:not(:disabled) { background: rgba(0, 180, 216, 0.3); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const AddRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  background: rgba(11, 19, 32, 0.4);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  align-items: center;
  .label {
    font-family: ${mono}; font-size: 0.62rem; letter-spacing: 0.18em;
    text-transform: uppercase; color: var(--text-disabled);
    margin-right: 0.4rem;
  }
  button {
    background: transparent; border: 1px solid ${borderSoft};
    color: var(--text-secondary);
    padding: 4px 10px; border-radius: 999px;
    font-size: 0.74rem; cursor: pointer;
    &:hover { color: ${accent}; border-color: rgba(0, 180, 216, 0.45); }
  }
`;

const SUGGESTIONS = [
  'Creá un módulo de referencias con las 5 ordenanzas más relevantes sobre humedales',
  'Agregá un timeline de la trayectoria de Manuel Passaglia',
  'Crea una tabla comparativa de los bloques del HCD',
  'Buscá las últimas noticias sobre el Puerto SN y agrégalas como news_feed',
  'Hacé un módulo de stats con votaciones del bloque LLA',
];

const ADDABLE = ['note', 'references', 'connections', 'entity_list', 'timeline', 'stats', 'comparison', 'quote', 'news_feed'];

export default function AnalysisDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatErr, setChatErr] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const [activeRag, setActiveRag] = useState([]);
  const bodyRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true); setErr(null);
      const d = await getPage(slug);
      setData(d);
    } catch (e) {
      setErr(friendlyError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [data?.chat, sending]);

  if (loading && !data) return <TechShell><Loading>Cargando…</Loading></TechShell>;
  if (err) return <TechShell><BackLink to="/analysis"><FaArrowLeft /> Volver</BackLink><ErrorBox>{err}</ErrorBox></TechShell>;
  if (!data?.page) return <TechShell><ErrorBox>Página no encontrada</ErrorBox></TechShell>;

  const { page, modules, chat: history } = data;

  const handleAddModule = async (kind) => {
    const defaultData = {
      note: { content: '_(Edita este módulo o pedile al chat IA que lo complete)_' },
      references: { items: [] },
      connections: { items: [] },
      entity_list: { items: [] },
      timeline: { items: [] },
      stats: { items: [] },
      comparison: { headers: ['Columna A', 'Columna B'], rows: [] },
      quote: { text: '', source: '', url: '' },
      news_feed: { items: [] },
    }[kind] || {};
    await createModule(slug, { kind, title: null, data: defaultData });
    await load();
  };

  const handleUpdateModule = async (mid, patch) => {
    await updateModule(slug, mid, patch);
    await load();
  };

  const handleDeleteModule = async (mid) => {
    await deleteModule(slug, mid);
    await load();
  };

  const handleSend = async (override) => {
    const q = (override ?? input).trim();
    if (!q || sending) return;
    setChatErr(null);
    setInput('');
    setSending(true);
    try {
      const res = await sendChat(slug, q);
      // refrescar todo (chat + modules)
      await load();
    } catch (e) {
      setChatErr(friendlyError(e));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openCite = (cite, rag) => {
    setActiveRag(rag || []);
    setActiveCite(cite);
  };

  return (
    <TechShell>
      <BackLink to="/analysis"><FaArrowLeft /> Volver al lab</BackLink>
      <TechPageHeader
        title={page.title}
        subtitle={page.description || 'Análisis personalizado · módulos editables + chat IA'}
        dossier={`AL-${String(page.id).padStart(4, '0')}`}
      />

      <Split>
        <ModulesCol>
          <AddRow>
            <span className="label">Agregar módulo:</span>
            {ADDABLE.map((k) => (
              <button key={k} type="button" onClick={() => handleAddModule(k)}>
                <FaPlus size={9} style={{ marginRight: 4 }} />
                {KIND_LABEL[k] || k}
              </button>
            ))}
          </AddRow>

          {modules.length === 0 ? (
            <Empty>
              Sin módulos todavía. Agregá uno arriba o pedile al chat IA que te lo arme.
              Ej: <em>"Creá un módulo de referencias con las últimas ordenanzas sobre humedales"</em>.
            </Empty>
          ) : (
            modules.map((m) => (
              <AnalysisModule
                key={m.id}
                module={m}
                onUpdate={(patch) => handleUpdateModule(m.id, patch)}
                onDelete={() => handleDeleteModule(m.id)}
              />
            ))
          )}
        </ModulesCol>

        <ChatCol>
          <ChatHeader>
            <span className="icon"><FaRobot /></span>
            <span className="title">Asistente</span>
            <span className="sub">RAG + tools</span>
          </ChatHeader>
          <ChatBody ref={bodyRef}>
            {history.length === 0 && !sending && (
              <div style={{ color: 'var(--text-disabled)', fontSize: '0.84rem', lineHeight: 1.5 }}>
                Pedile al asistente que cree módulos, los edite, busque en el índice HCD o scrapee información externa.
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => handleSend(s)}
                      style={{
                        background: 'transparent', border: '1px solid ' + borderSoft,
                        color: 'var(--text-secondary)',
                        padding: '5px 10px', borderRadius: 999, fontSize: '0.72rem',
                        cursor: 'pointer', textAlign: 'left',
                      }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {history.map((m) => {
              const handleCite = (cite) => openCite(cite, m.citations);
              return (
                <Msg key={m.id} $role={m.role}>
                  <div className="avatar">{m.role === 'user' ? <FaUser /> : <FaRobot />}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="bubble">
                      {m.role === 'assistant' ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={wrapWithCitations(handleCite)}>
                          {m.content}
                        </ReactMarkdown>
                      ) : m.content}
                    </div>
                    {Array.isArray(m.actions) && m.actions.length > 0 && (
                      <div className="actions-summary">
                        ✓ Acciones: {m.actions.filter((a) => a.ok).map((a) => a.tool).join(', ')}
                      </div>
                    )}
                  </div>
                </Msg>
              );
            })}
            {sending && (
              <Msg $role="assistant">
                <div className="avatar"><FaRobot /></div>
                <div className="bubble"><Typing><span /><span /><span /></Typing></div>
              </Msg>
            )}
            {chatErr && (
              <ErrorBox style={{ margin: 0 }}>
                <FaExclamationCircle style={{ marginRight: 6 }} /> {chatErr}
              </ErrorBox>
            )}
          </ChatBody>
          <InputWrap onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
            <ChatInput
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Pedile algo al asistente…"
              disabled={sending}
              rows={1}
            />
            <SendBtn type="submit" disabled={sending || !input.trim()} aria-label="Enviar">
              <FaPaperPlane />
            </SendBtn>
          </InputWrap>
        </ChatCol>
      </Split>

      {activeCite && (
        <CitePopup
          cite={activeCite}
          indexed={[]}
          fresh={[]}
          rag={activeRag}
          onClose={() => setActiveCite(null)}
        />
      )}
    </TechShell>
  );
}
